import { useEffect, useMemo, useRef, useState } from "react";
import { ProjectBar } from "@/components/ProjectBar";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, ChevronDown, ChevronRight, Download, FileUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { useProject } from "@/lib/workflow/useProject";
import { cleanDate, cleanEmail, cleanPhone, cleanText, type Cleaned } from "@/lib/workflow/fields";
import { describeImport, exportRecords, LAYOUTS, templateCsv, type ImportIssue } from "@/lib/workflow/layouts";
import {
  filterGuests, findDuplicateGuests, guestsFromLegacyBackup, mergeImportedGuests, migrateLegacyGuests, partySize, removeDuplicateGuests,
  STATUS_LABEL, summarizeGuests,
} from "@/lib/workflow/guestOps";
import { newId } from "@/lib/workflow/ids";
import { GUEST_IMPORT_ACCEPT, readGuestFile } from "@/lib/workflow/guestImport";
import { GUEST_STATUSES, type Guest, type GuestStatus } from "@/lib/workflow/types";
import { NoticeBox } from "@/components/NoticeBox";

const STATUS_COLOR: Record<GuestStatus, string> = {
  invited: "text-muted-foreground",
  confirmed: "text-green-600",
  declined: "text-red-600",
  waitlist: "text-amber-600",
  attended: "text-primary",
};

const LEGACY_KEYS = ["fdp-GuestListTracker-guests-v1", "fdp-GuestListTracker-nextId-v1"];

const TOOL_INFO_CONTENT = {
  what: `Tracks guests, their replies, plus-ones, food and access needs, and who needs a room, with running totals. It keeps the list in a project that other event tools on this site can open, so the same guests can go straight into badges, invitations and rooming lists.`,
  how: `Add guests one at a time or import a CSV or Excel file. Change a reply from the dropdown, open Details for contact, diet, access and stay needs, and watch the head count update. Download the list as CSV or Excel, or save the whole project to continue later.`,
  tip: `Chase the guests still marked Invited a few weeks before your reply deadline, and look at the Need a room and Diet totals before you quote numbers to a hotel or caterer.`,
};

/** A text box that checks its value when you leave it and only then saves, so half-typed emails and dates are never stored. */
function Field({ label, value, clean, onCommit, type = "text", placeholder, className }: {
  label: string; value: string | undefined; clean: (s: string) => Cleaned<string>; onCommit: (v: string | undefined) => void;
  type?: string; placeholder?: string; className?: string;
}) {
  const [text, setText] = useState(value ?? "");
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { setText(value ?? ""); setError(null); }, [value]);
  const commit = () => {
    const t = text.trim();
    if (t === (value ?? "")) { setError(null); return; }
    if (t === "") { setError(null); onCommit(undefined); return; }
    const c = clean(t);
    if (c.ok) { setError(null); onCommit(c.value); } else setError(`${label} ${c.message}`);
  };
  return (
    <label className={`block text-xs ${className ?? ""}`}>
      <span className="text-muted-foreground">{label}</span>
      <Input
        type={type} value={text} placeholder={placeholder} aria-invalid={!!error}
        onChange={(e) => setText(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && commit()}
        className={error ? "border-red-500" : undefined}
      />
      {error && <span className="text-red-600">{error}</span>}
    </label>
  );
}

const text300 = (s: string) => cleanText(s, 300);
const text2000 = (s: string) => cleanText(s, 2000);

export default function GuestListTracker() {
  const [project, store] = useProject();
  const guests = project.data.guests;
  const [newName, setNewName] = useState("");
  const [newStatus, setNewStatus] = useState<GuestStatus>("confirmed");
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<GuestStatus | "all">("all");
  const [onlyStay, setOnlyStay] = useState(false);
  const [report, setReport] = useState<{ summary: string; issues: ImportIssue[]; unknown: string[]; note?: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const oldBackup = useRef<HTMLInputElement>(null);
  const { logUsageNow } = useLogUsageOnAction();

  // Carry over a list kept by the earlier version of this tool in this tab, once.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(LEGACY_KEYS[0]);
      if (raw && store.get().data.guests.length === 0) {
        const migrated = migrateLegacyGuests(JSON.parse(raw));
        if (migrated.length) { store.setRecords("guests", migrated); toast.success(`Brought over ${migrated.length} guests from your earlier list`); }
      }
      LEGACY_KEYS.forEach((k) => sessionStorage.removeItem(k));
    } catch { /* storage blocked or list unreadable: start empty */ }
  }, [store]);

  const summary = useMemo(() => summarizeGuests(guests), [guests]);
  const duplicates = useMemo(() => findDuplicateGuests(guests), [guests]);
  const visible = useMemo(() => filterGuests(guests, { text: search, status: filterStatus, needsStay: onlyStay }), [guests, search, filterStatus, onlyStay]);

  const update = (id: string, patch: Partial<Guest>) => {
    const g = guests.find((x) => x.id === id);
    if (!g) return;
    const next = { ...g, ...patch } as Guest;
    for (const k of Object.keys(next) as (keyof Guest)[]) if (next[k] === undefined) delete next[k];
    store.upsertRecords("guests", [next]);
  };
  const remove = (id: string) => store.setRecords("guests", guests.filter((g) => g.id !== id));
  const add = () => {
    const name = newName.trim();
    if (!name) return;
    store.upsertRecords("guests", [{ id: newId("g"), name: name.slice(0, 300), status: newStatus, needsStay: false }]);
    setNewName("");
  };
  const toggle = (id: string) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const openOldBackup = async (f: File | undefined) => {
    if (!f) return;
    const r = guestsFromLegacyBackup(await f.text());
    if (!r.ok) { toast.error(r.error); return; }
    store.upsertRecords("guests", r.guests);
    toast.success(`Restored ${r.guests.length} guest${r.guests.length === 1 ? "" : "s"} from the old backup`);
  };
  const importFile = async (f: File | undefined) => {
    if (!f) return;
    const r = await readGuestFile(f, { currency: project.currency });
    const errors = r.issues.filter((i) => i.level === "error");
    if (r.records.length) store.setRecords("guests", mergeImportedGuests(guests, r.records));
    setReport({ summary: r.records.length ? describeImport(r, "guests") : (errors[0]?.message ?? "Nothing could be imported."), issues: r.issues, unknown: r.unknownColumns, note: r.note });
    if (r.records.length) toast.success(describeImport(r, "guests"));
    else toast.error(errors[0]?.message ?? "Nothing could be imported");
  };

  const exportCsv = async () => {
    if (!guests.length) return;
    if (!(await logUsageNow())) return;
    downloadBlob(new Blob([exportRecords("guests", guests)], { type: "text/csv;charset=utf-8" }), "guest-list.csv");
    toast.success("Guest list downloaded");
  };

  const exportExcel = async () => {
    if (!guests.length) return;
    if (!(await logUsageNow())) return;
    const cols = LAYOUTS.guests.columns.filter((c) => c.key !== "id");
    const head = [...cols.map((c) => c.header), "People coming"];
    const rows: (string | number)[][] = [
      head,
      ...guests.map((g) => [
        ...cols.map((c) => { const v = (g as unknown as Record<string, unknown>)[c.key]; return typeof v === "boolean" ? (v ? "yes" : "no") : ((v as string | number | undefined) ?? ""); }),
        partySize(g),
      ]),
      [],
      ["Total guests", summary.total],
      ...GUEST_STATUSES.map((s): (string | number)[] => [STATUS_LABEL[s], summary.byStatus[s]]),
      ["People coming (with plus-ones)", summary.headcount],
      ["Need a room", summary.needsStay],
      ["With dietary needs", summary.withDietary],
      ["With access needs", summary.withAccess],
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = head.map((h, i) => ({ wch: i === 0 ? 24 : Math.max(12, h.length + 2) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Guest List");
    const buffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    downloadBlob(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "guest-list.xlsx");
    toast.success("Guest list downloaded");
  };

  const tiles: { label: string; value: number; color: string }[] = [
    { label: "Confirmed", value: summary.byStatus.confirmed, color: "text-green-600" },
    { label: "Invited", value: summary.byStatus.invited, color: "text-muted-foreground" },
    { label: "Waitlist", value: summary.byStatus.waitlist, color: "text-amber-600" },
    { label: "Declined", value: summary.byStatus.declined, color: "text-red-600" },
    { label: "People coming", value: summary.headcount, color: "text-primary" },
    { label: "Need a room", value: summary.needsStay, color: "text-primary" },
    { label: "Diet notes", value: summary.withDietary, color: "text-primary" },
  ];

  return (
    <ToolContainer>
      <div className="flex gap-2 mb-4">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Add a guest..." maxLength={300} onKeyDown={(e) => e.key === "Enter" && add()} aria-label="Guest name" />
        <Select value={newStatus} onValueChange={(v) => setNewStatus(v as GuestStatus)}>
          <SelectTrigger className={`w-36 ${STATUS_COLOR[newStatus]}`} aria-label="Starting reply"><SelectValue /></SelectTrigger>
          <SelectContent>{GUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={add} aria-label="Add guest"><Plus className="h-4 w-4" /></Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <Button variant="outline" size="sm" onClick={() => fileInput.current?.click()}><FileUp className="h-4 w-4 mr-1" />Import CSV or Excel</Button>
        <Button variant="ghost" size="sm" onClick={() => downloadBlob(new Blob([templateCsv("guests")], { type: "text/csv;charset=utf-8" }), "guest-list-template.csv")}>Get a blank CSV</Button>
        <Button variant="ghost" size="sm" onClick={() => oldBackup.current?.click()} data-testid="open-old-backup">Open old backup</Button>
        <input ref={oldBackup} type="file" accept=".json,application/json" className="hidden" onChange={(e) => { void openOldBackup(e.target.files?.[0]); e.target.value = ""; }} />
        <input ref={fileInput} type="file" accept={GUEST_IMPORT_ACCEPT} className="hidden" onChange={(e) => { void importFile(e.target.files?.[0]); e.target.value = ""; }} />
        {guests.length > 0 && (
          <>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search guests..." className="h-8 w-44" aria-label="Search guests" />
            <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as GuestStatus | "all")}>
              <SelectTrigger className="h-8 w-36" aria-label="Filter by reply"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All replies</SelectItem>
                {GUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={onlyStay} onChange={(e) => setOnlyStay(e.target.checked)} />Need a room</label>
          </>
        )}
      </div>

      {report && (
        <Card className="p-3 mb-3 text-sm" role="status">
          <div className="flex justify-between gap-2">
            <p className="font-medium">{report.summary}</p>
            <button className="text-xs underline" onClick={() => setReport(null)}>Dismiss</button>
          </div>
          {report.note && <p className="text-xs text-muted-foreground mt-1">{report.note}</p>}
          {report.unknown.length > 0 && <p className="text-xs text-muted-foreground mt-1">Ignored columns: {report.unknown.join(", ")}</p>}
          {report.issues.length > 0 && (
            <ul className="mt-2 text-xs space-y-0.5 max-h-40 overflow-auto">
              {report.issues.slice(0, 25).map((i, k) => (
                <li key={k} className={i.level === "error" ? "text-red-600" : "text-amber-700"}>Row {i.row}{i.column ? `, ${i.column}` : ""}: {i.message}</li>
              ))}
              {report.issues.length > 25 && <li className="text-muted-foreground">and {report.issues.length - 25} more</li>}
            </ul>
          )}
        </Card>
      )}

      {duplicates.length > 0 && (
        <NoticeBox role="status" className="mb-3 text-sm flex flex-wrap items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-primary" />
          <span>{duplicates.length} possible duplicate{duplicates.length === 1 ? "" : "s"}: {duplicates.slice(0, 3).map((d) => d[0].name).join(", ")}{duplicates.length > 3 ? "..." : ""}.</span>
          <Button size="sm" variant="outline" onClick={() => { const r = removeDuplicateGuests(guests); store.setRecords("guests", r.guests); toast.success(`Removed ${r.removed} later copies`); }}>Keep the first of each</Button>
        </NoticeBox>
      )}

      {guests.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No guests yet. Add one above, import a CSV or Excel file, or use "Try a sample" below.</p>
      ) : (
        <div className="space-y-2">
          {visible.map((g) => (
            <div key={g.id} className="rounded-md border p-2">
              <div className="flex flex-wrap gap-2 items-center">
                <button className="p-1" onClick={() => toggle(g.id)} aria-label={open.has(g.id) ? "Hide details" : "Show details"} aria-expanded={open.has(g.id)}>
                  {open.has(g.id) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <Field label="" value={g.name} clean={text300} onCommit={(v) => v && update(g.id, { name: v })} className="flex-1 min-w-[9rem] [&>span:first-child]:hidden" />
                <Select value={g.status} onValueChange={(v) => update(g.id, { status: v as GuestStatus })}>
                  <SelectTrigger className={`w-[130px] ${STATUS_COLOR[g.status]}`} aria-label={`Reply for ${g.name}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{GUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                </Select>
                <Input
                  className="w-16" value={g.plusOnes ?? ""} placeholder="+0" inputMode="numeric" aria-label={`Plus-ones for ${g.name}`}
                  onChange={(e) => { const d = e.target.value.replace(/\D/g, "").slice(0, 3); update(g.id, { plusOnes: d ? Math.min(Number(d), 1000) : undefined }); }}
                />
                <Button size="icon" variant="ghost" onClick={() => remove(g.id)} aria-label={`Remove ${g.name}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              {open.has(g.id) && (
                <div className="grid sm:grid-cols-3 gap-2 mt-2 pl-8">
                  <Field label="Email" type="email" value={g.email} clean={cleanEmail} onCommit={(v) => update(g.id, { email: v })} />
                  <Field label="Phone" value={g.phone} clean={cleanPhone} onCommit={(v) => update(g.id, { phone: v })} />
                  <Field label="Organisation" value={g.organisation} clean={text300} onCommit={(v) => update(g.id, { organisation: v })} />
                  <Field label="Role" value={g.role} clean={text300} onCommit={(v) => update(g.id, { role: v })} />
                  <Field label="Dietary" value={g.dietary} clean={text300} placeholder="e.g. vegetarian, no nuts" onCommit={(v) => update(g.id, { dietary: v })} />
                  <Field label="Access needs" value={g.access} clean={text300} placeholder="e.g. step-free" onCommit={(v) => update(g.id, { access: v })} />
                  <Field label="Table" value={g.table} clean={text300} onCommit={(v) => update(g.id, { table: v })} />
                  <Field label="Arrival (YYYY-MM-DD)" value={g.arrival} clean={cleanDate} placeholder="2026-11-12" onCommit={(v) => update(g.id, { arrival: v })} />
                  <Field label="Departure (YYYY-MM-DD)" value={g.departure} clean={cleanDate} placeholder="2026-11-14" onCommit={(v) => update(g.id, { departure: v })} />
                  <label className="flex items-center gap-2 text-xs self-end pb-2"><input type="checkbox" checked={g.needsStay} onChange={(e) => update(g.id, { needsStay: e.target.checked })} />Needs a room</label>
                  <Field label="Notes" value={g.notes} clean={text2000} onCommit={(v) => update(g.id, { notes: v })} className="sm:col-span-2" />
                </div>
              )}
            </div>
          ))}
          {visible.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No guests match this search.</p>}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-6">
        {tiles.map((t) => (
          <Card key={t.label} className="p-3 text-center">
            <p className={`text-lg font-bold ${t.color}`}>{t.value}</p>
            <p className="text-xs text-muted-foreground">{t.label}</p>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mt-4">
        <Button variant="outline" onClick={exportCsv} disabled={!guests.length}>Download CSV</Button>
        <Button className="gap-2" onClick={exportExcel} disabled={!guests.length}><Download className="h-4 w-4" /> Download Excel</Button>
      </div>
      <ProjectBar />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
